import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { createRolePermission, getAllAvailablePermissions, updateRolePermission } from '../services/permission';
import { getRolePermissions, getRoles, removeRole } from '../services/role';
// eslint-disable-next-line import/no-cycle
import { createAppAsyncThunk } from '../utils/reduxUtil';

import { CreateCustomerPayload, IPaginationInput } from '../models/payload/customer';
import { CreateRolePermissionPayload, UpdateRolePermissionsPayload } from '../models/payload/role';
import { CreateStaffResponse, Customers } from '../models/response/customer';
import { IError, IErrorResponse } from '../models/response/error';
import { RolePermission, RolePermissionsResponse } from '../models/response/permission';
import { PermissionsInRole, PermissionsInRoleResponse, Role, RolesResponse } from '../models/response/role';
import { createCustomer, getCustomers, removeCustomer } from '../services/customer';
import NUMBER_TABLE_PER_PAGE from '../shared/constants/numberPerPage';

interface VendorStaffStateInterface {
  loading: boolean;
  permissions: Array<RolePermission> | undefined | null;
  roles: Role[] | null | undefined;
  roleDetails: PermissionsInRole | undefined;
  staffs: Customers | undefined;
  errors: IError | undefined;
  openAddModal: boolean;
  openEditModal: boolean;
}

const initialState: VendorStaffStateInterface = {
  loading: false,
  permissions: undefined,
  roles: [],
  roleDetails: undefined,
  staffs: undefined,
  errors: undefined,
  openAddModal: false,
  openEditModal: false,
};

const getAvailablePermissions = createAppAsyncThunk<RolePermissionsResponse, void>(
  'permission/getAvailablePermissions',
  async () => {
    const response = await getAllAvailablePermissions();
    return response;
  }
);
const getAllVendorStaffRoles = createAppAsyncThunk<RolesResponse, void>(
  'vendorStaff/getAllVendorStaffRoles',
  async () => {
    const res = await getRoles();
    return res;
  }
);

const createVendorStaffRole = createAppAsyncThunk<void, CreateRolePermissionPayload>(
  'vendorStaff/createVendorStaffRole',
  async (input, { rejectWithValue }) => {
    try {
      const response = await createRolePermission(input);
      return response.data;
    } catch (error) {
      return rejectWithValue((error as AxiosError).response?.data as IErrorResponse);
    }
  }
);

const removeVendorStaffRole = createAppAsyncThunk<void, string>(
  'vendorStaff/removeVendorStaffRole',
  async (roleId, { dispatch }) => {
    await removeRole(roleId);

    await dispatch(getAllVendorStaffRoles());
  }
);

const getVendorStaffRole = createAppAsyncThunk<PermissionsInRoleResponse, string>(
  'vendorStaff/getVendorStaffRole',
  async (roleId) => {
    const res = await getRolePermissions(roleId);
    return res;
  }
);

const getAllVendorStaff = createAppAsyncThunk<Customers, IPaginationInput>(
  'vendorStaff/getAllVendorStaff',
  async (input) => {
    const res = await getCustomers(input);
    return res;
  }
);

const editVendorStaffRole = createAppAsyncThunk<void, UpdateRolePermissionsPayload>(
  'vendorStaff/editVendorStaffRole',
  async (input, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateRolePermission(input);
      await dispatch(getVendorStaffRole(input.roleId || ''));
      await dispatch(getAllVendorStaffRoles());
      return response.data;
    } catch (error) {
      return rejectWithValue((error as AxiosError).response?.data as IErrorResponse);
    }
  }
);

const createVendorStaff = createAppAsyncThunk<CreateStaffResponse, CreateCustomerPayload>(
  'vendorStaff/createVendorStaff',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCustomer(payload);
      return response;
    } catch (error) {
      return rejectWithValue((error as AxiosError).response?.data as IErrorResponse);
    }
  }
);
const removeVendorStaff = createAppAsyncThunk<void, string>(
  'vendorStaff/removeVendorStaff',
  async (id, { dispatch }) => {
    await removeCustomer(id);
    await dispatch(
      getAllVendorStaff({
        PageNumber: 1,
        PageSize: NUMBER_TABLE_PER_PAGE,
        Search: '',
      })
    );
  }
);

const vendorStaffSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    toggleAddModal: (state, action) => ({
      ...state,
      openAddModal: action.payload,
    }),
    toggleEditModal: (state, action) => ({
      ...state,
      openEditModal: action.payload,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<VendorStaffStateInterface>) => {
    builder.addCase(getAvailablePermissions.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getAvailablePermissions.fulfilled, (state, action) => {
      const { data } = action.payload;

      return {
        ...state,
        loading: false,
        permissions: data,
      };
    });

    builder.addCase(getAllVendorStaffRoles.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getAllVendorStaffRoles.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      roles: action?.payload?.data,
    }));

    builder.addCase(createVendorStaffRole.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(createVendorStaffRole.fulfilled, (state) => ({
      ...state,
      loading: false,
      openAddModal: false,
    }));

    builder.addCase(createVendorStaffRole.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));
    builder.addCase(removeVendorStaff.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(removeVendorStaff.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(removeVendorStaff.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(removeVendorStaffRole.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(removeVendorStaffRole.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));
    builder.addCase(removeVendorStaffRole.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(getVendorStaffRole.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getVendorStaffRole.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      roleDetails: action?.payload?.data,
    }));

    builder.addCase(editVendorStaffRole.pending, (state) => ({
      ...state,
      loading: true,
      errors: undefined,
    }));

    builder.addCase(editVendorStaffRole.fulfilled, (state) => ({
      ...state,
      loading: false,
      openEditModal: false,
    }));
    builder.addCase(editVendorStaffRole.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));
    builder.addCase(createVendorStaff.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(createVendorStaff.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(createVendorStaff.rejected, (state, action) => ({
      ...state,
      loading: false,
      errors: (action?.payload as IErrorResponse).data!.errors as IError,
    }));
    builder.addCase(getAllVendorStaff.pending, (state) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(getAllVendorStaff.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      staffs: action.payload,
    }));
  },
});

export default vendorStaffSlice;
export {
  createVendorStaff,
  createVendorStaffRole,
  editVendorStaffRole,
  getAllVendorStaff,
  getAllVendorStaffRoles,
  getAvailablePermissions,
  getVendorStaffRole,
  removeVendorStaff,
  removeVendorStaffRole,
};
